import { useEffect, useRef, useState } from 'react';
import { api } from './api';
import { friendlyStaffError } from './staffFeedback';
import type { IntakeDetail } from '../shared/types';
import {
	WORD_DOCUMENTS,
	WORD_CATALOG,
	wordChoices,
	wordAllowedPeople,
	isWordDocumentKind,
	wordPersonName,
	wordUsesTrustName,
	type WordDocumentKind,
	type WordDocumentPerson,
	type WordDocumentVersion,
} from '../shared/wordDocuments';

const errorMessage = (error: unknown) =>
	friendlyStaffError(error, 'DOC could not be created. Refresh and check the saved documents before trying again.');

export function WordDocuments({
	intake,
	csrfToken,
	onRefresh,
	beforeCreate,
	trusts = [],
}: {
	intake: IntakeDetail;
	csrfToken: string;
	onRefresh: () => Promise<void>;
	beforeCreate: (intakeId: string) => Promise<() => boolean>;
	trusts?: Array<{ id?: string; referenceName?: string | null }>;
}) {
	const [kind, setKind] = useState<WordDocumentKind | ''>('');
	const [search, setSearch] = useState('');
	const [person, setPerson] = useState<WordDocumentPerson>('client');
	const [trustId, setTrustId] = useState('');
	const savedTrusts = trusts.filter((trust) => trust.id);
	const [documents, setDocuments] = useState<WordDocumentVersion[]>([]);
	const [busy, setBusy] = useState(false);
	const [message, setMessage] = useState('');
	const [failed, setFailed] = useState(false);
	const requestId = useRef<string | null>(null);
	const status = useRef<HTMLParagraphElement>(null);
	const spouseName = wordPersonName(intake.answers, 'spouse');
	const choices = wordChoices(search);
	const people: WordDocumentPerson[] = kind ? wordAllowedPeople(kind) : ['client'];
	const base = `/api/staff/intakes/${intake.id}/documents`;
	useEffect(() => {
		let active = true;
		void api<{ documents: WordDocumentVersion[] }>(base)
			.then((result) => {
				if (active) setDocuments(result.documents);
			})
			.catch((error) => {
				if (active) {
					setFailed(true);
					setMessage(errorMessage(error));
				}
			});
		return () => {
			active = false;
		};
	}, [base, intake.rowVersion]);
	function changeChoice() {
		requestId.current = null;
		setMessage('');
	}
	async function create() {
		if (busy || !kind) return;
		const intakeId = intake.id;
		setBusy(true);
		setMessage('');
		setFailed(false);
		requestId.current ??= crypto.randomUUID();
		try {
			const isCurrent = await beforeCreate(intakeId);
			if (!isCurrent()) return;
			const result = await api<{ document: WordDocumentVersion }>(
				base,
				{
					method: 'POST',
					body: JSON.stringify({
						kind,
						person,
						requestId: requestId.current,
						...(kind && wordUsesTrustName(kind) && trustId ? { trustId } : {}),
					}),
				},
				csrfToken,
			);
			if (!isCurrent()) return;
			setDocuments((current) => [result.document, ...current.filter((doc) => doc.id !== result.document.id)]);
			requestId.current = null;
			setMessage('DOC created. Your download is starting. You can download it again below.');
			const link = document.createElement('a');
			link.href = `${base}/${result.document.id}`;
			link.download = '';
			document.body.appendChild(link);
			link.click();
			link.remove();
			// Refresh failure must not be reported as generation failure: the saved DOC already exists.
			await onRefresh().catch(() => undefined);
		} catch (error) {
			setFailed(true);
			setMessage(errorMessage(error));
		} finally {
			setBusy(false);
		}
	}
	useEffect(() => {
		if (message) status.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
	}, [message]);
	return (
		<section className="panel">
			<h2>Word documents</h2>
			{intake.status !== 'archived' && (
				<div className="compact-form">
					<label>
						Search documents
						<input
							type="search"
							value={search}
							disabled={busy}
							placeholder="Search by name, state or document type"
							onChange={(event) => {
								const value = event.target.value;
								setSearch(value);
								if (kind && !wordChoices(value).includes(kind)) {
									setKind('');
									setPerson('client');
									changeChoice();
								}
							}}
						/>
					</label>
					<p role="status">
						{choices.length} document {choices.length === 1 ? 'choice' : 'choices'} — alphabetical order
					</p>
					<label>
						Choose a doc
						<select
							disabled={busy}
							value={kind}
							size={Math.min(7, choices.length + 1)}
							onChange={(event) => {
								const value = event.target.value;
								if (!isWordDocumentKind(value)) return;
								setKind(value);
								if (!wordAllowedPeople(value).includes(person)) setPerson(wordAllowedPeople(value)[0]);
								changeChoice();
							}}
						>
							<option value="" disabled>
								{choices.length ? 'Select a document' : 'No matching documents'}
							</option>
							{choices.map((value) => (
								<option key={value} value={value}>
									{WORD_DOCUMENTS[value]}
								</option>
							))}
						</select>
					</label>
					<label>
						Choose who it is for
						<select
							disabled={busy}
							value={person}
							onChange={(event) => {
								setPerson(event.target.value as WordDocumentPerson);
								changeChoice();
							}}
						>
							{people.includes('client') && (
								<option value="client">
									{kind && WORD_CATALOG[kind].mode === 'intake'
										? 'Current intake'
										: kind && WORD_CATALOG[kind].mode === 'joint'
											? `${wordPersonName(intake.answers, 'client') || 'Client'} and ${spouseName || 'spouse'}`
											: wordPersonName(intake.answers, 'client') || 'Client'}
								</option>
							)}
							{people.includes('spouse') && (spouseName || (kind && WORD_CATALOG[kind].mode === 'spouse')) && (
								<option value="spouse">
									{spouseName || 'Spouse — enter a name in the questionnaire first'}
									{spouseName ? ' (spouse)' : ''}
								</option>
							)}
						</select>
					</label>
					{kind && wordUsesTrustName(kind) && (
						<>
							<p className="help">
								The trust name comes from saved information in “Initial Consultation: proposed trusts”. Save any changes there before
								creating this letter.
							</p>
							{savedTrusts.length > 1 && (
								<label>
									Choose a trust
									<select
										value={trustId}
										disabled={busy}
										onChange={(event) => {
											setTrustId(event.target.value);
											changeChoice();
										}}
									>
										<option value="">Select a trust</option>
										{savedTrusts.map((trust) => (
											<option key={trust.id} value={trust.id}>
												{trust.referenceName || 'Unnamed trust'}
											</option>
										))}
									</select>
								</label>
							)}
						</>
					)}
					<button type="button" disabled={busy || !kind || (person === 'spouse' && !spouseName)} onClick={() => void create()}>
						{busy ? 'Creating DOC…' : 'Create new DOC'}
					</button>
				</div>
			)}
			{message && (
				<p ref={status} role={failed ? 'alert' : 'status'} className={failed ? 'save-status-error' : 'save-status-confirmation'}>
					{message}
				</p>
			)}
			{documents.length ? (
				<ul>
					{documents.map((doc) => (
						<li key={doc.id}>
							<a href={`${base}/${doc.id}`}>
								{WORD_DOCUMENTS[doc.kind]} · {doc.person === 'client' ? 'Client' : 'Spouse'} · Download version {doc.version}
							</a>
							{' · '}
							{new Date(doc.generatedAt).toLocaleString()}
						</li>
					))}
				</ul>
			) : (
				<p>No Word documents created yet.</p>
			)}
		</section>
	);
}
